import { normalizeCurrencyDisplay } from "./currencyDisplay.js";
import {
  collectNonRenderableTripConflicts,
  resolveNonRenderableConflictAffectedIds,
} from "./resultsChromeData.js";

export const FILTER_ALL = "all";
export const FEEDBACK_VIEW_ALERTS = "errors";
export const FEEDBACK_VIEW_WARNINGS = "warnings";
export const ERRORS_EXPANSION_ALL_VISIBLE = "all-visible";
export const ERRORS_EXPANSION_MANUAL = "manual";

const SEVERITY_RANK = {
  error: 0,
  warning: 1,
  info: 2,
};

export const FILTER_LABELS = {
  excluded_truck_refresh: "Mises à jour Data manager",
  service_health: "Santé des services",
  technical_precheck: "Adresses & routage",
  non_assignable_orders: "Non assignables",
  routing_issues: "Alertes routage",
  unassigned_trucks: "Camions non assignés",
  invalid_truck_configuration: "Configurations camion invalides",
  non_renderable_trip: "Tournées à vérifier",
};

export const SOURCE_CONTEXT_LABELS = {
  excluded_truck_refresh: "Mise à jour Data manager",
  results_overview: "Synthèse résultats",
  service_health: "Santé des services",
  non_assigned_section: "Section non assignée",
  fleet_configuration: "Configuration camion",
  route_detail: "Route de trajet",
};

function isRecord(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function toArray(value) {
  return Array.isArray(value) ? value : [];
}

function compact(values) {
  return toArray(values).filter(Boolean);
}

export function normalizeText(value) {
  if (value === null || value === undefined) {
    return "";
  }

  return normalizeCurrencyDisplay(String(value).trim());
}

function firstNonEmpty(...values) {
  for (const value of values) {
    const normalized = normalizeText(value);
    if (normalized) {
      return normalized;
    }
  }

  return "";
}

function coerceNumber(value) {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  const normalized = normalizeText(value).replace(",", ".");
  if (!normalized) {
    return null;
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function formatMinutesHuman(durationMinutes) {
  if (!Number.isFinite(durationMinutes)) {
    return "";
  }

  const totalMinutes = Math.max(Math.round(durationMinutes), 0);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours && minutes) {
    return `${hours} h ${String(minutes).padStart(2, "0")} min`;
  }
  if (hours) {
    return `${hours} h`;
  }
  return `${minutes} min`;
}

function formatSpeedLabel(speedKmh) {
  if (!Number.isFinite(speedKmh) || speedKmh <= 0) {
    return "";
  }

  const roundedSpeed = Math.round(speedKmh * 10) / 10;
  return Number.isInteger(roundedSpeed)
    ? String(roundedSpeed)
    : roundedSpeed.toFixed(1).replace(".", ",");
}

function resolveRouteSummaryReferenceSpeed(resultPayload) {
  const speedCounts = new Map();
  for (const assignment of toArray(resultPayload?.assignments)) {
    const speedKmh = coerceNumber(assignment?.avg_speed_kmh);
    if (speedKmh === null || speedKmh <= 0) {
      continue;
    }

    const normalizedSpeed = Math.round(speedKmh * 1000) / 1000;
    speedCounts.set(normalizedSpeed, (speedCounts.get(normalizedSpeed) || 0) + 1);
  }

  let selectedSpeed = null;
  let selectedCount = -1;
  for (const [speedKmh, count] of speedCounts.entries()) {
    if (count > selectedCount || (count === selectedCount && speedKmh > (selectedSpeed ?? 0))) {
      selectedSpeed = speedKmh;
      selectedCount = count;
    }
  }

  return selectedSpeed;
}

function upgradeLegacyRouteSummary(routeSummary, resultPayload = null) {
  const summary = firstNonEmpty(routeSummary);
  if (!summary) {
    return "";
  }

  if (summary.startsWith("Temps estimé à la vitesse moyenne camion") || summary.startsWith("Projection de la meilleure tournée à la vitesse moyenne camion")) {
    return summary;
  }

  const match = summary.match(/^(Chargement -> déchargement|Chargement -> déchargements|Projection meilleure tournée)\s*:\s*([\d.,]+)\s*km\s*,/i);
  if (!match) {
    return summary;
  }

  const distanceKmh = coerceNumber(match[2]);
  const speedKmh = resolveRouteSummaryReferenceSpeed(resultPayload);
  if (distanceKmh === null || speedKmh === null || speedKmh <= 0) {
    return summary;
  }

  const durationLabel = formatMinutesHuman((distanceKmh / speedKmh) * 60.0);
  const speedLabel = formatSpeedLabel(speedKmh);
  const distanceLabel = `${match[2].replace(".", ",")} km`;
  if (!durationLabel || !speedLabel) {
    return summary;
  }

  if (match[1] === "Chargement -> déchargement") {
    return `Temps estimé à la vitesse moyenne camion (${speedLabel} km/h) entre le chargement et le déchargement: ${distanceLabel}, ${durationLabel}`;
  }

  if (match[1] === "Chargement -> déchargements") {
    return `Temps estimé à la vitesse moyenne camion (${speedLabel} km/h) entre le chargement et les déchargements: ${distanceLabel}, ${durationLabel}`;
  }

  return `Projection de la meilleure tournée à la vitesse moyenne camion (${speedLabel} km/h): ${distanceLabel}, ${durationLabel}`;
}

export function normalizeFilterKey(value) {
  const normalized = normalizeText(value);
  return normalized || FILTER_ALL;
}

function normalizeSeverity(value) {
  const normalized = normalizeText(value).toLowerCase();
  if (normalized === "error" || normalized === "warning" || normalized === "info") {
    return normalized;
  }

  return "info";
}

function normalizeRejectionCategory(value) {
  const normalized = normalizeText(value).toLowerCase();
  if (normalized === "technical_precheck" || normalized === "business_assignment" || normalized === "routing_degraded") {
    return normalized;
  }

  return "";
}

function normalizeErrorClassification(value) {
  const normalized = normalizeText(value).toLowerCase();
  if (normalized === "address_blocking" || normalized === "business_suspendable" || normalized === "technical_retry") {
    return normalized;
  }

  return "";
}

export function canShowSuspendFromErrorAction(entry, canMutatePlanning = false) {
  return Boolean(
    canMutatePlanning
      && entry?.canSuspend === true
      && entry?.errorId
      && entry?.suspendTargetOrderId
      && entry?.suspendAnchorDeliveryId,
  );
}

function humanizeReasonCode(value) {
  const normalized = normalizeText(value).toLowerCase();
  if (normalized === "single_trip_limit") {
    return "Camion déjà engagé";
  }
  if (normalized === "time_insufficient") {
    return "Heures maximales camionneur dépassées";
  }
  return normalized ? normalized.replace(/_/g, " ") : "";
}

const SINGLE_TRIP_LIMIT_USER_LABEL = "Camion déjà engagé";
const TIME_INSUFFICIENT_USER_LABEL = "Heures maximales camionneur dépassées";

function buildTimeInsufficientRecommendedAction(truckName = "") {
  const resolvedTruckName = firstNonEmpty(truckName);
  if (resolvedTruckName) {
    return `Ouvrir le temps supplémentaire sur ${resolvedTruckName}, puis réaffecter cette commande sur ce camion.`;
  }

  return "Ouvrir le temps supplémentaire sur un camion, puis réaffecter cette commande sur ce camion.";
}

function parseTimeInsufficientHumanDetail(text) {
  const normalizedText = normalizeText(text);
  const match = normalizedText.match(/(?:temps journalier insuffisant:\s*)?(.+?)\s+requis(?:es)?\s+pour\s+(.+?)\s+(?:disponibles|restants)/i);
  if (!match) {
    return null;
  }

  return {
    availableLabel: firstNonEmpty(match[2]),
    requiredLabel: firstNonEmpty(match[1]),
  };
}

function formatMaxWorkHoursUserCause({
  availableLabel = "",
  fallbackDetail = "",
  requiredLabel = "",
  truckName = "",
} = {}) {
  const resolvedRequiredLabel = firstNonEmpty(requiredLabel);
  const resolvedAvailableLabel = firstNonEmpty(availableLabel);
  if (resolvedRequiredLabel && resolvedAvailableLabel) {
    const prefix = truckName ? `Sur ${truckName}, ` : "";
    return `${prefix}les heures maximales de travail du camionneur seraient dépassées (${resolvedRequiredLabel} requises pour une journée de ${resolvedAvailableLabel}).`;
  }

  const parsedDetail = parseTimeInsufficientHumanDetail(fallbackDetail);
  if (parsedDetail) {
    return formatMaxWorkHoursUserCause({
      availableLabel: parsedDetail.availableLabel,
      requiredLabel: parsedDetail.requiredLabel,
      truckName,
    });
  }

  if (/heures maximales/i.test(fallbackDetail)) {
    return fallbackDetail;
  }

  return fallbackDetail || "Les heures maximales de travail du camionneur seraient dépassées.";
}

function isActionableTimeRejectionCause(cause) {
  const normalizedCause = normalizeText(cause).toLowerCase();
  return normalizedCause.includes("temps journalier insuffisant")
    || normalizedCause.includes("heures maximales")
    || (normalizedCause.includes("rebuild local") && normalizedCause.includes("echoue"))
    || /\d+\s*min requis/.test(normalizedCause)
    || /\d+\s*h(?:\s*\d+\s*min)?\s+requis/.test(normalizedCause);
}

function extractAssignmentTimePresentation(entry, orderView) {
  const technicalDetails = isRecord(entry?.technical_details) ? entry.technical_details : null;
  if (technicalDetails?.diagnostic_type === "time_window") {
    return null;
  }
  const truckName = firstNonEmpty(technicalDetails?.truck_name, technicalDetails?.truck_id);

  if (
    technicalDetails?.diagnostic_type === "locked_single_trip_rebuild_failure"
    && normalizeText(technicalDetails?.selected_reason_code).toLowerCase() === "time_insufficient"
  ) {
    return {
      cause: formatMaxWorkHoursUserCause({
        fallbackDetail: firstNonEmpty(technicalDetails?.reason_detail, entry?.cause),
        truckName,
      }),
      isTechnicalDetailsAvailable: true,
      recommendedAction: buildTimeInsufficientRecommendedAction(truckName),
      technicalDetails,
      technicalSummary: TIME_INSUFFICIENT_USER_LABEL,
    };
  }

  if (
    technicalDetails?.diagnostic_type === "time_insufficient"
    || technicalDetails?.diagnostic_type === "single_trip_limit_with_time_insufficient"
  ) {
    return {
      cause: formatMaxWorkHoursUserCause({
        availableLabel: firstNonEmpty(technicalDetails?.daily_limit_label),
        fallbackDetail: firstNonEmpty(technicalDetails?.raw_reason, entry?.cause),
        requiredLabel: firstNonEmpty(technicalDetails?.projected_duration_label),
        truckName,
      }),
      isTechnicalDetailsAvailable: true,
      recommendedAction: buildTimeInsufficientRecommendedAction(truckName),
      technicalDetails,
      technicalSummary: TIME_INSUFFICIENT_USER_LABEL,
    };
  }

  if (isActionableTimeRejectionCause(entry?.cause)) {
    return {
      cause: formatMaxWorkHoursUserCause({
        fallbackDetail: entry.cause,
        truckName,
      }),
      isTechnicalDetailsAvailable: Boolean(entry?.is_technical_details_available),
      recommendedAction: buildTimeInsufficientRecommendedAction(truckName),
      technicalDetails: technicalDetails ?? entry?.technical_details,
      technicalSummary: TIME_INSUFFICIENT_USER_LABEL,
    };
  }

  const planningNote = firstNonEmpty(orderView?.planning_note);
  if (planningNote && isActionableTimeRejectionCause(planningNote)) {
    return {
      cause: formatMaxWorkHoursUserCause({
        fallbackDetail: planningNote,
      }),
      isTechnicalDetailsAvailable: true,
      recommendedAction: buildTimeInsufficientRecommendedAction(truckName),
      technicalDetails: technicalDetails ?? entry?.technical_details,
      technicalSummary: TIME_INSUFFICIENT_USER_LABEL,
    };
  }

  return null;
}

function normalizeTruckCompatibilityReasonLabel(reasonCode, reasonLabel) {
  if (normalizeText(reasonCode).toLowerCase() === "single_trip_limit") {
    return SINGLE_TRIP_LIMIT_USER_LABEL;
  }
  if (normalizeText(reasonCode).toLowerCase() === "time_insufficient") {
    return TIME_INSUFFICIENT_USER_LABEL;
  }

  return normalizeTruckCompatibilityCopy(firstNonEmpty(reasonLabel, humanizeReasonCode(reasonCode), "Contrainte non qualifiée"));
}

function normalizeTruckCompatibilityDetail(reasonCode, detail, truckName = "") {
  const normalizedReasonCode = normalizeText(reasonCode).toLowerCase();
  if (normalizedReasonCode === "time_insufficient") {
    return formatMaxWorkHoursUserCause({
      fallbackDetail: detail,
      truckName,
    });
  }

  const normalizedDetail = normalizeTruckCompatibilityCopy(detail);
  if (normalizedReasonCode !== "single_trip_limit") {
    return normalizedDetail;
  }

  const truckLabel = firstNonEmpty(truckName);
  if (
    /second trajet|mono-tournée|mono-tournee|déjà une tournée|deja une tournee|tournée unique|tournee unique/i.test(normalizedDetail)
  ) {
    return truckLabel
      ? `${truckLabel} transporte déjà d'autres livraisons aujourd'hui et ne peut pas recevoir cette commande.`
      : "Ce camion transporte déjà d'autres livraisons aujourd'hui et ne peut pas recevoir cette commande.";
  }

  return normalizedDetail;
}

function resolveDefaultErrorTitle(filterKey, rejectionCategory) {
  if (rejectionCategory === "technical_precheck" || filterKey === "technical_precheck") {
    return "Adresse ou routage invalide";
  }

  if (rejectionCategory === "business_assignment" || filterKey === "non_assignable_orders") {
    return "Affectation impossible";
  }

  if (rejectionCategory === "routing_degraded" || filterKey === "routing_issues") {
    return "Alerte de routage";
  }

  return "Erreur";
}

function resolveDefaultRecommendedAction(filterKey, rejectionCategory) {
  if (rejectionCategory === "technical_precheck" || filterKey === "technical_precheck") {
    return "Corriger l'adresse concernée ou relancer le routage avant la prochaine planification.";
  }

  if (rejectionCategory === "business_assignment" || filterKey === "non_assignable_orders") {
    return "Ajuster les contraintes d'affectation puis relancer l'optimisation.";
  }

  if (rejectionCategory === "routing_degraded" || filterKey === "routing_issues") {
    return "Relancer le calcul de routage si l'alerte persiste.";
  }

  return "Examiner le blocage remonté puis relancer le traitement concerné.";
}

function normalizeLocationStatus(status) {
  if (status === "error") {
    return "error";
  }

  if (status === "success") {
    return "success";
  }

  return "neutral";
}

function normalizeLocationItem(item, { fallbackLabel = "", fallbackText = "", fallbackStatus = "neutral" } = {}) {
  if (isRecord(item)) {
    return {
      address: firstNonEmpty(item.address, fallbackText),
      label: firstNonEmpty(item.label, fallbackLabel),
      status: normalizeLocationStatus(item.status || fallbackStatus),
    };
  }

  if (!fallbackLabel && !fallbackText) {
    return null;
  }

  return {
    address: firstNonEmpty(fallbackText),
    label: firstNonEmpty(fallbackLabel),
    status: normalizeLocationStatus(fallbackStatus),
  };
}

function normalizeDeliveryItems(entry) {
  const normalizedItems = toArray(entry.delivery_items)
    .filter(isRecord)
    .map((item) => normalizeLocationItem(item, { fallbackLabel: "Déchargement", fallbackStatus: "neutral" }))
    .filter(Boolean);

  if (normalizedItems.length) {
    return normalizedItems;
  }

  const deliveryDetails = toArray(entry.delivery_details);
  return deliveryDetails
    .map((value, index) => ({
      address: firstNonEmpty(value),
      label: deliveryDetails.length > 1 ? `Déchargement ${index + 1}` : "Déchargement",
      status: normalizeLocationStatus(
        entry.scope === "delivery" && index === 0
          ? "error"
          : entry.scope === "loading"
            ? "success"
            : "neutral",
      ),
    }))
    .filter((item) => item.address);
}

function findUnassignedOrderView(resultPayload, orderId) {
  if (!orderId) {
    return null;
  }

  return toArray(resultPayload?.unassigned_orders)
    .filter(isRecord)
    .find((item) => firstNonEmpty(item.order_id) === orderId) || null;
}

function isGenericUnassignedCause(cause) {
  if (isActionableTimeRejectionCause(cause)) {
    return false;
  }

  const normalizedCause = normalizeText(cause).toLowerCase();
  return normalizedCause.includes("aucun camion disponible ne peut encore realiser")
    || normalizedCause.includes("aucune combinaison mission/camion compatible")
    || normalizedCause.includes("aucun camion compatible n'a pu etre retenu")
    || normalizedCause.includes("aucun camion compatible n'a pu être retenu")
    || normalizedCause.includes("aucun camion compatible n'a été retenu");
}

function normalizeTruckCompatibilityCopy(value) {
  const normalizedValue = firstNonEmpty(value);
  if (!normalizedValue) {
    return "";
  }

  return normalizedValue
    .replaceAll("Itineraire routier introuvable", "Itinéraire routier introuvable")
    .replaceAll("Tournee unique deja utilisee", SINGLE_TRIP_LIMIT_USER_LABEL)
    .replaceAll("Tournée unique déjà utilisée", SINGLE_TRIP_LIMIT_USER_LABEL)
    .replaceAll("aucun itineraire routier exploitable", "Aucun itinéraire routier exploitable")
    .replaceAll("HERE Routing n'a retourne aucun itineraire exploitable.", "HERE Routing n'a retourné aucun itinéraire exploitable.")
    .replaceAll("Camion deja servi pour la journee: ", "Camion déjà servi pour la journée : ")
    .replaceAll(
      "Camion deja servi pour la journee: la regle mono-tournee interdit d'ouvrir une seconde tournee pour ",
      "Camion déjà engagé : ",
    )
    .replaceAll(
      "a deja une tournee; l'optimisation n'autorise pas l'ouverture d'un second trajet sur ce camion.",
      "transporte déjà d'autres livraisons aujourd'hui et ne peut pas recevoir cette commande.",
    )
    .replaceAll(
      "a déjà une tournée ; l'optimisation n'autorise pas l'ouverture d'un second trajet sur ce camion.",
      "transporte déjà d'autres livraisons aujourd'hui et ne peut pas recevoir cette commande.",
    );
}

function formatTruckCountLabel(count) {
  return `${count} camion${count > 1 ? "s" : ""}`;
}

function buildTruckCompatibilityTechnicalItems(compatibility) {
  return compatibility.map((item) => ({
    detail: normalizeTruckCompatibilityDetail(item.reason_code, item.detail, item.truck_name),
    isAssignable: Boolean(item.is_assignable),
    reasonCode: firstNonEmpty(item.reason_code),
    reasonLabel: normalizeTruckCompatibilityReasonLabel(item.reason_code, item.reason_label),
    truckId: firstNonEmpty(item.truck_id),
    truckName: firstNonEmpty(item.truck_name),
  }));
}

function buildTruckCompatibilityBreakdown(technicalItems) {
  const breakdownByReason = new Map();

  technicalItems.forEach((item) => {
    const normalizedReasonCode = normalizeText(item.reasonCode).toLowerCase();
    const normalizedReasonLabel = normalizeTruckCompatibilityReasonLabel(normalizedReasonCode, item.reasonLabel);
    const breakdownKey = `${normalizedReasonCode}:${normalizedReasonLabel}`;
    const existingGroup = breakdownByReason.get(breakdownKey) || {
      count: 0,
      details: [],
      reasonCode: normalizedReasonCode,
      reasonLabel: normalizedReasonLabel,
      truckNames: [],
    };

    existingGroup.count += 1;

    const truckLabel = firstNonEmpty(item.truckName, item.truckId);
    if (truckLabel && !existingGroup.truckNames.includes(truckLabel)) {
      existingGroup.truckNames.push(truckLabel);
    }

    if (item.detail && !existingGroup.details.includes(item.detail)) {
      existingGroup.details.push(item.detail);
    }

    breakdownByReason.set(breakdownKey, existingGroup);
  });

  return Array.from(breakdownByReason.values())
    .sort((leftEntry, rightEntry) => rightEntry.count - leftEntry.count || leftEntry.reasonLabel.localeCompare(rightEntry.reasonLabel, "fr", { sensitivity: "base" }))
    .map((item) => ({
      ...item,
      representativeDetail: firstNonEmpty(item.details[0]),
    }));
}

function buildTruckCompatibilitySummaryCause(breakdown, compatibilityCount) {
  const routeBreakdown = breakdown.find((item) => item.reasonCode === "route_unavailable");
  const singleTripBreakdown = breakdown.find((item) => item.reasonCode === "single_trip_limit");
  const significantBreakdown = breakdown
    .filter((item) => Number.isFinite(item.count) && item.count > 0);
  const dominantBreakdown = significantBreakdown
    .slice(0, 2)
    .map((item) => `${item.reasonLabel} (${item.count})`)
    .join(", ");
  const hiddenReasonCount = Math.max(significantBreakdown.length - 2, 0);
  const hiddenReasonSuffix = hiddenReasonCount > 0
    ? `, + ${hiddenReasonCount} autre${hiddenReasonCount > 1 ? "s" : ""} motif${hiddenReasonCount > 1 ? "s" : ""}`
    : "";

  if (routeBreakdown && breakdown.length === 1) {
    return `Le service de routage n'a trouvé aucun trajet exploitable pour cette commande parmi ${formatTruckCountLabel(compatibilityCount)} évalués.`;
  }

  if (singleTripBreakdown && breakdown.length === 1) {
    return "Tous les camions compatibles évalués transportent déjà d'autres livraisons aujourd'hui.";
  }

  if (dominantBreakdown) {
    return `Aucun camion n'est resté assignable pour cette commande (${formatTruckCountLabel(compatibilityCount)} évalués : ${dominantBreakdown}${hiddenReasonSuffix}).`;
  }

  return `Aucun camion n'est resté assignable pour cette commande (${formatTruckCountLabel(compatibilityCount)} évalués).`;
}

function buildTruckCompatibilitySummaryAction(breakdown, fallbackAction) {
  const routeBreakdown = breakdown.find((item) => item.reasonCode === "route_unavailable");
  const singleTripBreakdown = breakdown.find((item) => item.reasonCode === "single_trip_limit");
  const timeBreakdown = breakdown.find((item) => item.reasonCode === "time_insufficient");

  if (routeBreakdown && singleTripBreakdown) {
    return "Vérifier le calcul d'itinéraire entre le chargement et le déchargement, puis relancer l'optimisation. Les camions déjà engagés ne peuvent pas recevoir cette commande.";
  }

  if (singleTripBreakdown && timeBreakdown) {
    return buildTimeInsufficientRecommendedAction();
  }

  if (routeBreakdown) {
    return "Vérifier le calcul d'itinéraire entre le chargement et le déchargement, puis relancer l'optimisation.";
  }

  if (singleTripBreakdown && breakdown.length === 1) {
    return "Libérer un camion déjà engagé ou affecter un camion disponible avant de relancer l'optimisation.";
  }

  return fallbackAction;
}

function buildTruckCompatibilityDiagnostic(orderView, fallbackProduct = "") {
  const compatibility = toArray(orderView?.truck_compatibility).filter(isRecord);
  if (!compatibility.length) {
    return null;
  }

  const planningNotePresentation = extractAssignmentTimePresentation(
    { cause: firstNonEmpty(orderView?.planning_note), recommended_action: orderView?.recommended_action },
    orderView,
  );
  if (planningNotePresentation) {
    return planningNotePresentation;
  }

  const productLabel = firstNonEmpty(orderView?.grain_type, fallbackProduct, "ce produit");
  const technicalItems = buildTruckCompatibilityTechnicalItems(compatibility);
  const breakdown = buildTruckCompatibilityBreakdown(technicalItems);
  const assignableCount = technicalItems.filter((item) => item.isAssignable).length;
  const timeEntries = compatibility.filter((item) => {
    const reasonCode = normalizeText(item.reason_code).toLowerCase();
    const detail = normalizeText(item.detail).toLowerCase();
    return reasonCode === "time_insufficient" || detail.includes("temps journalier insuffisant");
  });
  const singleTripEntries = compatibility.filter((item) => normalizeText(item.reason_code).toLowerCase() === "single_trip_limit");

  if (timeEntries.length) {
    const preferredEntry = timeEntries[0];
    const detail = firstNonEmpty(preferredEntry.detail);
    const timeCause = formatMaxWorkHoursUserCause({
      fallbackDetail: detail,
      truckName: firstNonEmpty(preferredEntry.truck_name, preferredEntry.truck_id),
    });

    if (timeEntries.length === compatibility.length) {
      return {
        cause: timeCause,
        isTechnicalDetailsAvailable: true,
        recommendedAction: buildTimeInsufficientRecommendedAction(firstNonEmpty(preferredEntry.truck_name, preferredEntry.truck_id)),
        technicalDetails: technicalItems,
        technicalSummary: TIME_INSUFFICIENT_USER_LABEL,
      };
    }

    if (singleTripEntries.length && timeEntries.length + singleTripEntries.length === compatibility.length) {
      return {
        cause: timeCause,
        isTechnicalDetailsAvailable: true,
        recommendedAction: buildTimeInsufficientRecommendedAction(firstNonEmpty(preferredEntry.truck_name, preferredEntry.truck_id)),
        technicalDetails: {
          assignableTruckCount: assignableCount,
          breakdown,
          candidates: technicalItems,
          diagnosticType: "truck_compatibility_breakdown",
          evaluatedTruckCount: compatibility.length,
          singleTripLimitCount: singleTripEntries.length,
          timeCause,
          timeInsufficientCount: timeEntries.length,
        },
        technicalSummary: TIME_INSUFFICIENT_USER_LABEL,
      };
    }
  }

  const aspiratorReasonEntries = compatibility.filter((item) => normalizeText(item.reason_code).toLowerCase() === "aspirator_required_no_compatible_truck");

  if (aspiratorReasonEntries.length === compatibility.length) {
    const incompatibleCount = aspiratorReasonEntries.length;
    return {
      cause: `Aucun camion compatible pour ${productLabel}: aspirateur requis sur ${incompatibleCount} camion${incompatibleCount > 1 ? "s" : ""}.`,
      isTechnicalDetailsAvailable: true,
      recommendedAction: `Affecter un camion aspirateur compatible avec ${productLabel} avant de relancer l'optimisation.`,
      technicalDetails: technicalItems,
      technicalSummary: "Aspirateur requis",
    };
  }

  const ontarioReasonEntries = compatibility.filter((item) => normalizeText(item.reason_code).toLowerCase() === "ontario_required_no_compatible_truck");

  if (ontarioReasonEntries.length === compatibility.length) {
    const incompatibleCount = ontarioReasonEntries.length;
    return {
      cause: `Aucun camion compatible pour ${productLabel}: Ontario requis sur ${incompatibleCount} camion${incompatibleCount > 1 ? "s" : ""}.`,
      isTechnicalDetailsAvailable: true,
      recommendedAction: `Affecter un camion autorisé Ontario compatible avec ${productLabel} avant de relancer l'optimisation.`,
      technicalDetails: technicalItems,
      technicalSummary: "Ontario requis",
    };
  }

  const productReasonEntries = compatibility.filter((item) => {
    const reasonCode = normalizeText(item.reason_code).toLowerCase();
    return reasonCode === "truck_product_locked" || reasonCode === "product_incompatible" || reasonCode === "locked_product_incompatible";
  });

  if (productReasonEntries.length === compatibility.length) {
    return {
      cause: `Spécialisation historique détectée pour ${productLabel}; elle n'est plus bloquante.`,
      isTechnicalDetailsAvailable: true,
      recommendedAction: "Relancer l'optimisation pour recalculer avec les règles produit non bloquantes.",
      technicalDetails: technicalItems,
      technicalSummary: "Spécialisation historique",
    };
  }

  return {
    cause: buildTruckCompatibilitySummaryCause(breakdown, compatibility.length),
    isTechnicalDetailsAvailable: true,
    recommendedAction: buildTruckCompatibilitySummaryAction(
      breakdown,
      firstNonEmpty(orderView?.recommended_action, "Vérifier les causes dominantes du rejet puis relancer l'optimisation."),
    ),
    technicalDetails: {
      assignableTruckCount: assignableCount,
      breakdown,
      candidates: technicalItems,
      diagnosticType: "truck_compatibility_breakdown",
      evaluatedTruckCount: compatibility.length,
    },
    technicalSummary: `${formatTruckCountLabel(compatibility.length)} évalués pour cette commande, ${assignableCount} assignable${assignableCount === 1 ? "" : "s"}`,
  };
}

function normalizeErrorEntry(entry, index, resultPayload = null) {
  const filterKey = normalizeFilterKey(entry.filter_key);
  const rejectionCategory = normalizeRejectionCategory(entry.rejection_category);
  const normalizedSeverity = filterKey === "unassigned_trucks"
    ? "warning"
    : normalizeSeverity(entry.severity);
  const orderId = firstNonEmpty(entry.order_id);
  const fallbackOrderView = filterKey === "non_assignable_orders"
    ? findUnassignedOrderView(resultPayload, orderId)
    : null;
  // Un camion non mis à jour garde la cause et le conseil du serveur, les mêmes que la bulle du triangle.
  const timePresentation = filterKey === "excluded_truck_refresh"
    ? null
    : extractAssignmentTimePresentation(entry, fallbackOrderView);
  const compatibilityDiagnostic = fallbackOrderView && isGenericUnassignedCause(entry.cause) && !timePresentation
    ? buildTruckCompatibilityDiagnostic(fallbackOrderView, entry.product)
    : null;

  return {
    category: firstNonEmpty(entry.category, resolveFilterLabel(entry.filter_key)),
    cause: firstNonEmpty(timePresentation?.cause, compatibilityDiagnostic?.cause, entry.cause, "Cause indisponible."),
    clientName: firstNonEmpty(entry.client_name),
    countsAsNonAssignable: rejectionCategory
      ? rejectionCategory === "business_assignment"
      : Boolean(entry.counts_as_non_assignable),
    deliveryDetails: toArray(entry.delivery_details).map((value) => firstNonEmpty(value)).filter(Boolean),
    deliveryId: firstNonEmpty(entry.delivery_id),
    deliveryItems: normalizeDeliveryItems(entry),
    errorId: firstNonEmpty(entry.error_id, `error-${index + 1}`),
    errorClassification: normalizeErrorClassification(entry.error_classification),
    filterKey,
    canSuspend: entry.can_suspend === true,
    suspendBlockReason: firstNonEmpty(entry.suspend_block_reason),
    suspendTargetOrderId: firstNonEmpty(entry.suspend_target_order_id),
    suspendAnchorDeliveryId: firstNonEmpty(entry.suspend_anchor_delivery_id),
    initialReasonCode: firstNonEmpty(entry.initial_reason_code),
    initialReasonLabel: firstNonEmpty(entry.initial_reason_label),
    isTechnicalDetailsAvailable: timePresentation
      ? Boolean(timePresentation.isTechnicalDetailsAvailable)
      : compatibilityDiagnostic
        ? Boolean(compatibilityDiagnostic.isTechnicalDetailsAvailable)
        : Boolean(entry.is_technical_details_available),
    loadingDetail: firstNonEmpty(entry.loading_detail),
    loadingItem: normalizeLocationItem(entry.loading_item, {
      fallbackLabel: "Chargement",
      fallbackStatus: entry.scope === "loading" ? "error" : entry.scope === "delivery" ? "success" : "neutral",
      fallbackText: firstNonEmpty(entry.loading_detail),
    }),
    orderId,
    orderLabel: firstNonEmpty(entry.order_label),
    product: firstNonEmpty(entry.product),
    recommendedAction: firstNonEmpty(
      timePresentation?.recommendedAction,
      compatibilityDiagnostic?.recommendedAction,
      entry.recommended_action,
      resolveDefaultRecommendedAction(filterKey, rejectionCategory),
    ),
    rejectionCategory,
    routeSummary: upgradeLegacyRouteSummary(entry.route_summary, resultPayload),
    scope: firstNonEmpty(entry.scope),
    severity: normalizedSeverity,
    sourceContext: firstNonEmpty(entry.source_context),
    technicalDetails: timePresentation?.technicalDetails ?? compatibilityDiagnostic?.technicalDetails ?? entry.technical_details,
    technicalSummary: firstNonEmpty(timePresentation?.technicalSummary, compatibilityDiagnostic?.technicalSummary, entry.technical_summary, "Détails techniques routage"),
    quantityTons: typeof entry.quantity_tons === "number" && Number.isFinite(entry.quantity_tons) && entry.quantity_tons > 0 ? entry.quantity_tons : null,
    isDompeur: typeof entry.is_dompeur === "boolean" ? entry.is_dompeur : null,
    title: firstNonEmpty(entry.title, resolveDefaultErrorTitle(filterKey, rejectionCategory)),
    tripId: firstNonEmpty(entry.trip_id),
    truckId: firstNonEmpty(entry.truck_id),
    truckName: firstNonEmpty(entry.truck_name),
  };
}

function compareEntries(leftEntry, rightEntry) {
  const severityDelta = (SEVERITY_RANK[leftEntry.severity] ?? 99) - (SEVERITY_RANK[rightEntry.severity] ?? 99);
  if (severityDelta !== 0) {
    return severityDelta;
  }

  if (leftEntry.countsAsNonAssignable !== rightEntry.countsAsNonAssignable) {
    return leftEntry.countsAsNonAssignable ? -1 : 1;
  }

  const titleDelta = leftEntry.title.localeCompare(rightEntry.title, "fr", { sensitivity: "base" });
  if (titleDelta !== 0) {
    return titleDelta;
  }

  return leftEntry.errorId.localeCompare(rightEntry.errorId, "fr", { sensitivity: "base" });
}

export function normalizeFeedbackViewName(viewName = "") {
  return viewName === FEEDBACK_VIEW_WARNINGS ? FEEDBACK_VIEW_WARNINGS : FEEDBACK_VIEW_ALERTS;
}

export function getFeedbackViewLabel(viewName = FEEDBACK_VIEW_ALERTS) {
  return normalizeFeedbackViewName(viewName) === FEEDBACK_VIEW_WARNINGS ? "Avertissements" : "Alertes";
}

function getFeedbackNoun(viewName = FEEDBACK_VIEW_ALERTS, count = 1) {
  const normalizedViewName = normalizeFeedbackViewName(viewName);

  if (normalizedViewName === FEEDBACK_VIEW_WARNINGS) {
    return count > 1 ? "avertissements" : "avertissement";
  }

  return count > 1 ? "alertes" : "alerte";
}

function getFeedbackActiveLabel(viewName = FEEDBACK_VIEW_ALERTS, count = 1) {
  const normalizedViewName = normalizeFeedbackViewName(viewName);

  if (normalizedViewName === FEEDBACK_VIEW_WARNINGS) {
    return count > 1 ? "actifs" : "actif";
  }

  return count > 1 ? "actives" : "active";
}

function buildServiceHealthEntries(resultPayload) {
  return toArray(resultPayload?.service_health)
    .filter(isRecord)
    .map((entry, index) => {
      const status = normalizeText(entry.status).toLowerCase();
      if (status !== "error" && status !== "degraded") {
        return null;
      }

      const label = firstNonEmpty(entry.label, entry.key, `Service ${index + 1}`);
      const detail = firstNonEmpty(entry.detail);
      const signals = compact(entry.signals).map((value) => firstNonEmpty(value)).filter(Boolean);
      const isError = status === "error";

      return {
        category: "services",
        cause: firstNonEmpty(entry.summary, detail, "Incident de service détecté."),
        clientName: "",
        countsAsNonAssignable: false,
        deliveryDetails: [],
        deliveryId: "",
        deliveryItems: [],
        errorId: firstNonEmpty(entry.error_id, `service-health:${firstNonEmpty(entry.key, String(index + 1))}`),
        filterKey: "service_health",
        isTechnicalDetailsAvailable: Boolean(detail || signals.length > 0),
        loadingDetail: "",
        loadingItem: null,
        orderId: "",
        orderLabel: "",
        product: "",
        recommendedAction: isError
          ? `Vérifier ${label} puis relancer l'optimisation ou le service concerné.`
          : normalizeText(entry.key).toLowerCase() === "here"
          ? "HERE répond en partie : certains trajets peuvent être estimés. Relancez plus tard si ça dure."
          : `${label} répond en partie. Relancez plus tard si ça dure.`,
        rejectionCategory: "",
        scope: "service",
        // Un service dégradé répond encore en partie : avertissement, pas alerte.
        severity: isError ? "error" : "warning",
        sourceContext: "service_health",
        technicalDetails: {
          detail,
          signals,
          status,
        },
        technicalSummary: `Diagnostic ${label}`,
        title: isError ? `${label} en erreur` : `${label} dégradé`,
        tripId: "",
        truckId: "",
        truckName: "",
      };
    })
    .filter(Boolean);
}

function normalizeConflictIdList(...values) {
  const normalizedValues = [];
  values.forEach((value) => {
    const candidates = Array.isArray(value) ? value : [value];
    candidates.forEach((candidate) => {
      const normalized = firstNonEmpty(candidate);
      if (normalized) {
        normalizedValues.push(normalized);
      }
    });
  });
  return [...new Set(normalizedValues)];
}

function buildNonRenderableTripEntries(resultPayload) {
  const groups = new Map();
  collectNonRenderableTripConflicts(resultPayload, resultPayload?.assignments).forEach((conflict, index) => {
    if (!isRecord(conflict)) {
      return;
    }
    const {
      deliveryIds,
      hasMissingDeliveryIds,
      hasMissingOrderIds,
      orderIds,
    } = resolveNonRenderableConflictAffectedIds(conflict);
    const truckId = firstNonEmpty(conflict.truck_id);
    const truckName = firstNonEmpty(conflict.truck_name, conflict.truck_label, truckId);
    const tripId = firstNonEmpty(conflict.trip_id);
    const scopeOrderIds = normalizeConflictIdList(conflict.order_ids, conflict.order_id).sort();
    const scopeDeliveryIds = normalizeConflictIdList(conflict.delivery_ids, conflict.delivery_id).sort();
    const fallbackIdentity = scopeOrderIds.join("|")
      || scopeDeliveryIds.join("|")
      || orderIds.join("|")
      || deliveryIds.join("|")
      || String(index + 1);
    const groupKey = `${truckId || truckName || "unknown"}::${tripId || fallbackIdentity}`;
    const existingGroup = groups.get(groupKey) || {
      activeOrderCount: 0,
      conflicts: [],
      fallbackDeliveryIds: new Set(),
      fallbackOrderIds: new Set(),
      missingDeliveryIds: new Set(),
      missingOrderIds: new Set(),
      reasonCodes: new Set(),
      tripId,
      truckId,
      truckName,
    };

    orderIds.forEach((orderId) => {
      (hasMissingOrderIds ? existingGroup.missingOrderIds : existingGroup.fallbackOrderIds)
        .add(orderId);
    });
    deliveryIds.forEach((deliveryId) => {
      (hasMissingDeliveryIds ? existingGroup.missingDeliveryIds : existingGroup.fallbackDeliveryIds)
        .add(deliveryId);
    });
    normalizeConflictIdList(conflict.reason_codes).forEach((reasonCode) => existingGroup.reasonCodes.add(reasonCode));
    existingGroup.activeOrderCount = Math.max(
      existingGroup.activeOrderCount,
      Number.isFinite(Number(conflict.active_order_count))
        ? Math.max(0, Math.round(Number(conflict.active_order_count)))
        : 0,
    );
    existingGroup.conflicts.push(conflict);
    groups.set(groupKey, existingGroup);
  });

  return [...groups.values()].map((group, index) => {
    const orderIds = [
      ...(group.missingOrderIds.size > 0 ? group.missingOrderIds : group.fallbackOrderIds),
    ];
    const deliveryIds = [
      ...(group.missingDeliveryIds.size > 0
        ? group.missingDeliveryIds
        : group.fallbackDeliveryIds),
    ];
    const orderCount = group.missingOrderIds.size > 0
      ? group.missingOrderIds.size
      : Math.max(orderIds.length, group.activeOrderCount, 1);
    const truckLabel = group.truckName || group.truckId || "camion à identifier";
    const orderLabel = orderCount > 1 ? `${orderCount} commandes` : (orderIds[0] || "1 commande");

    return {
      category: "planning",
      cause: `Le camion conserve ${orderCount} commande${orderCount > 1 ? "s affectées" : " affectée"}, mais les étapes de chargement ou de livraison nécessaires à la tournée sont incomplètes.`,
      clientName: "",
      countsAsNonAssignable: false,
      deliveryDetails: [],
      deliveryId: deliveryIds[0] || "",
      deliveryItems: orderIds.map((orderId) => ({
        address: "",
        label: `Commande ${orderId}`,
        status: "error",
      })),
      errorId: `non-renderable-trip:${group.truckId || group.truckName || "unknown"}:${group.tripId || index + 1}`,
      filterKey: "non_renderable_trip",
      isTechnicalDetailsAvailable: true,
      loadingDetail: "",
      loadingItem: null,
      orderId: orderIds[0] || "",
      orderLabel,
      product: "",
      recommendedAction: "Ouvrez Mise à jour pour réparer cette tournée ou placer la commande dans Suspendues.",
      rejectionCategory: "",
      scope: "assignment",
      severity: "error",
      sourceContext: "results_overview",
      technicalDetails: {
        delivery_ids: deliveryIds,
        order_ids: orderIds,
        reason_codes: [...group.reasonCodes],
        trip_id: group.tripId,
        truck_id: group.truckId,
        truck_name: group.truckName,
      },
      technicalSummary: "Camion, tournée et commandes concernées",
      title: `Tournée à vérifier — ${truckLabel}`,
      tripId: group.tripId,
      truckId: group.truckId,
      truckName: group.truckName,
    };
  });
}

function matchesFeedbackView(entry, viewName = FEEDBACK_VIEW_ALERTS) {
  const normalizedViewName = normalizeFeedbackViewName(viewName);

  if (entry.technicalDetails?.diagnostic_type === "current_address_validation") {
    // Ignore synthetic validation alerts still present in older responses.
    return false;
  }

  if (normalizedViewName === FEEDBACK_VIEW_WARNINGS) {
    return entry.severity === "warning";
  }

  return entry.severity === "error";
}

export function buildErrorsCenterEntries(resultPayload, options = {}) {
  // Un camion libre n'est un avertissement que s'il reste des commandes à placer.
  const hasUnassignedOrders = toArray(resultPayload?.unassigned_orders).some(isRecord);
  const baseEntries = toArray(resultPayload && resultPayload.error_entries)
    .filter(isRecord)
    .filter((entry) => hasUnassignedOrders || normalizeFilterKey(entry.filter_key) !== "unassigned_trucks")
    .map((entry, index) => normalizeErrorEntry(entry, index, resultPayload))
    .sort(compareEntries);
  const serviceHealthEntries = buildServiceHealthEntries(resultPayload);
  const nonRenderableTripEntries = buildNonRenderableTripEntries(resultPayload);
  const combinedEntries = [
    ...baseEntries,
    ...serviceHealthEntries,
    ...nonRenderableTripEntries,
  ].sort(compareEntries);
  const normalizedViewName = normalizeFeedbackViewName(options.viewName);

  return combinedEntries.filter((entry) => matchesFeedbackView(entry, normalizedViewName));
}

export function buildVisibleErrors(entries, activeFilter) {
  if (activeFilter === FILTER_ALL) {
    return entries;
  }

  return entries.filter((entry) => entry.filterKey === activeFilter);
}

function normalizeGroupingText(value) {
  return normalizeText(value).normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
}

function isOrderBasedClientLabel(value) {
  return /^commande\s+/i.test(normalizeText(value));
}

export function resolveNonAssignableClientLabel(entry) {
  if (!entry) {
    return "";
  }

  const deliveryItem = Array.isArray(entry.deliveryItems) && entry.deliveryItems.length > 0
    ? entry.deliveryItems[0]
    : null;
  const deliveryLabel = firstNonEmpty(deliveryItem?.label);
  if (deliveryLabel && !isOrderBasedClientLabel(deliveryLabel)) {
    return deliveryLabel;
  }

  const clientName = firstNonEmpty(entry.clientName);
  if (clientName && !isOrderBasedClientLabel(clientName)) {
    return clientName;
  }

  return deliveryLabel || clientName || "";
}

function resolveNonAssignableGroupIdentity(entry) {
  if (!entry || entry.filterKey !== "non_assignable_orders" || entry.countsAsNonAssignable !== true) {
    return null;
  }

  const deliveryItem = Array.isArray(entry.deliveryItems) && entry.deliveryItems.length > 0
    ? entry.deliveryItems[0]
    : null;
  const label = resolveNonAssignableClientLabel(entry);
  const address = firstNonEmpty(deliveryItem?.address, entry.deliveryDetails?.[0]);
  if (!label) {
    return null;
  }

  return {
    address,
    key: `${normalizeGroupingText(label)}|${normalizeGroupingText(address)}`,
    label,
  };
}

function formatGroupedQuantity(entries) {
  const totalQuantity = toArray(entries).reduce((total, entry) => (
    Number.isFinite(entry?.quantityTons) ? total + entry.quantityTons : total
  ), 0);
  if (totalQuantity <= 0) {
    return "";
  }

  return `${Number.isInteger(totalQuantity) ? totalQuantity : totalQuantity.toFixed(1)} t`;
}

function buildNonAssignableGroupSubtitle(entries) {
  const safeEntries = toArray(entries);
  const products = Array.from(new Set(
    safeEntries
      .map((entry) => normalizeText(entry?.product))
      .filter(Boolean),
  ));
  const quantityLabel = formatGroupedQuantity(safeEntries);
  const bodyModes = Array.from(new Set(
    safeEntries
      .map((entry) => (entry?.isDompeur === true ? "Dompeur" : entry?.isDompeur === false ? "Citerne" : ""))
      .filter(Boolean),
  ));

  return [
    quantityLabel,
    products.length === 1 ? products[0] : products.length > 1 ? "Produits mixtes" : "",
    bodyModes.length === 1 ? bodyModes[0] : bodyModes.length > 1 ? "Types mixtes" : "",
  ].filter(Boolean).join(" · ");
}

function compareVisibleErrorRows(leftRow, rightRow) {
  const leftIsGroup = leftRow?.rowType === "group";
  const rightIsGroup = rightRow?.rowType === "group";
  if (leftIsGroup !== rightIsGroup) {
    return leftIsGroup ? -1 : 1;
  }

  if (leftIsGroup && rightIsGroup) {
    const titleDelta = normalizeText(leftRow?.title).localeCompare(
      normalizeText(rightRow?.title),
      "fr",
      { sensitivity: "base" },
    );
    if (titleDelta !== 0) {
      return titleDelta;
    }
  }

  const leftEntry = leftIsGroup ? leftRow?.primaryEntry : leftRow?.entry;
  const rightEntry = rightIsGroup ? rightRow?.primaryEntry : rightRow?.entry;
  return compareEntries(leftEntry, rightEntry);
}

export function buildVisibleErrorRows(visibleEntries) {
  const safeEntries = toArray(visibleEntries);
  const groupedByClient = new Map();

  safeEntries.forEach((entry) => {
    const identity = resolveNonAssignableGroupIdentity(entry);
    if (!identity?.key) {
      return;
    }

    const group = groupedByClient.get(identity.key) || {
      entries: [],
      identity,
    };
    group.entries.push(entry);
    groupedByClient.set(identity.key, group);
  });

  const renderedGroupKeys = new Set();
  const rows = [];

  safeEntries.forEach((entry) => {
    const identity = resolveNonAssignableGroupIdentity(entry);
    const group = identity?.key ? groupedByClient.get(identity.key) : null;
    if (!group || group.entries.length < 2) {
      rows.push({
        entry,
        rowId: entry.errorId,
        rowType: "entry",
      });
      return;
    }

    if (renderedGroupKeys.has(identity.key)) {
      return;
    }

    renderedGroupKeys.add(identity.key);
    rows.push({
      entries: group.entries,
      memberCount: group.entries.length,
      primaryEntry: group.entries[0],
      rowId: `error-group:non-assignable:${identity.key}`,
      rowType: "group",
      subtitle: buildNonAssignableGroupSubtitle(group.entries),
      title: identity.label,
    });
  });

  return rows.sort(compareVisibleErrorRows);
}

function listVisibleErrorIds(visibleEntries) {
  return toArray(visibleEntries)
    .map((entry) => normalizeText(entry?.errorId))
    .filter(Boolean);
}

export function createCollapsedExpansionState() {
  return {
    expandedErrorIds: [],
    expansionMode: ERRORS_EXPANSION_MANUAL,
  };
}

export function resolveFilterSelectionExpansionState(filterKey) {
  const normalizedFilter = normalizeFilterKey(filterKey);
  if (normalizedFilter === FILTER_ALL) {
    return {
      expandedErrorIds: [],
      expansionMode: ERRORS_EXPANSION_ALL_VISIBLE,
    };
  }

  return createCollapsedExpansionState();
}

export function getResolvedExpandedErrorIds(visibleEntries, {
  expandedErrorIds = [],
  expansionMode = ERRORS_EXPANSION_MANUAL,
} = {}) {
  const visibleErrorIds = listVisibleErrorIds(visibleEntries);
  if (expansionMode === ERRORS_EXPANSION_ALL_VISIBLE) {
    return visibleErrorIds;
  }

  const expandedErrorIdSet = new Set(
    toArray(expandedErrorIds)
      .map((errorId) => normalizeText(errorId))
      .filter(Boolean),
  );
  return visibleErrorIds.filter((errorId) => expandedErrorIdSet.has(errorId));
}

export function toggleExpandedErrorId(errorId, visibleEntries, state = {}) {
  const normalizedErrorId = normalizeText(errorId);
  const nextExpandedErrorIds = new Set(getResolvedExpandedErrorIds(visibleEntries, state));

  if (!normalizedErrorId) {
    return createCollapsedExpansionState();
  }

  if (nextExpandedErrorIds.has(normalizedErrorId)) {
    nextExpandedErrorIds.delete(normalizedErrorId);
  } else {
    nextExpandedErrorIds.add(normalizedErrorId);
  }

  return {
    expandedErrorIds: Array.from(nextExpandedErrorIds),
    expansionMode: ERRORS_EXPANSION_MANUAL,
  };
}

export function resolveOpenRequestExpansionState(entries, {
  errorId = "",
  filter = FILTER_ALL,
  openAll = false,
} = {}) {
  const normalizedFilter = normalizeFilterKey(filter);
  const visibleEntries = buildVisibleErrors(entries, normalizedFilter);
  const normalizedErrorId = normalizeText(errorId);

  if (normalizedErrorId && visibleEntries.some((entry) => entry.errorId === normalizedErrorId)) {
    return {
      expandedErrorIds: [normalizedErrorId],
      expansionMode: ERRORS_EXPANSION_MANUAL,
    };
  }

  if (openAll) {
    return {
      expandedErrorIds: [],
      expansionMode: ERRORS_EXPANSION_ALL_VISIBLE,
    };
  }

  return createCollapsedExpansionState();
}

export function buildErrorsCenterFilters(entries, activeFilter) {
  const countByFilter = new Map();

  entries.forEach((entry) => {
    countByFilter.set(entry.filterKey, (countByFilter.get(entry.filterKey) || 0) + 1);
  });

  const filters = [{
    filterKey: FILTER_ALL,
    label: `Toutes (${entries.length})`,
  }];

  Array.from(countByFilter.keys())
    .sort((leftKey, rightKey) => resolveFilterLabel(leftKey).localeCompare(resolveFilterLabel(rightKey), "fr", { sensitivity: "base" }))
    .forEach((filterKey) => {
      filters.push({
        filterKey,
        label: `${resolveFilterLabel(filterKey)} (${countByFilter.get(filterKey) || 0})`,
      });
    });

  if (activeFilter !== FILTER_ALL && !countByFilter.has(activeFilter)) {
    filters.push({
      filterKey: activeFilter,
      label: `${resolveFilterLabel(activeFilter)} (0)`,
    });
  }

  return filters;
}

function buildFilterDescription(entry, source) {
  const sourceLabel = source ? ` Source : ${source}.` : "";
  return `${entry.title}.${sourceLabel}`;
}

export function buildErrorsCenterSummary(entries, visibleEntries, activeFilter, activeSource, options = {}) {
  const normalizedViewName = normalizeFeedbackViewName(options.viewName);
  const singularNoun = getFeedbackNoun(normalizedViewName, 1);
  const pluralNoun = getFeedbackNoun(normalizedViewName, 2);
  const singularActiveLabel = getFeedbackActiveLabel(normalizedViewName, 1);
  const pluralActiveLabel = getFeedbackActiveLabel(normalizedViewName, 2);

  if (!entries.length) {
    return {
      description: "",
      title: `Aucun${normalizedViewName === FEEDBACK_VIEW_WARNINGS ? "" : "e"} ${singularNoun} ${singularActiveLabel}.`,
    };
  }

  const nonAssignableCount = entries.filter((entry) => entry.countsAsNonAssignable).length;
  if (activeFilter === FILTER_ALL) {
    return {
      description: "",
      title: nonAssignableCount
        ? `${entries.length} ${pluralNoun}, dont ${nonAssignableCount} mission(s) non assignables.`
        : `${entries.length} ${pluralNoun} ${pluralActiveLabel}.`,
    };
  }

  if (!visibleEntries.length) {
    return {
      description: `Aucun ${singularNoun} pour ce filtre.`,
      title: `Aucun${normalizedViewName === FEEDBACK_VIEW_WARNINGS ? "" : "e"} ${singularNoun} ${singularActiveLabel} pour ${resolveFilterLabel(activeFilter)}.`,
    };
  }

  return {
    description: buildFilterDescription(visibleEntries[0], activeSource),
    title: `${visibleEntries.length} ${visibleEntries.length > 1 ? pluralNoun : singularNoun} visible${visibleEntries.length > 1 ? "s" : ""} pour ${resolveFilterLabel(activeFilter)}.`,
  };
}

export function resolveFilterLabel(filterKey) {
  const normalizedKey = normalizeFilterKey(filterKey);
  return FILTER_LABELS[normalizedKey] || (normalizedKey === FILTER_ALL ? "Toutes les entrées" : normalizedKey);
}

export function resolveSourceContextLabel(sourceContext) {
  const normalizedSource = normalizeText(sourceContext);
  if (!normalizedSource) {
    return "";
  }

  return SOURCE_CONTEXT_LABELS[normalizedSource] || normalizedSource;
}

export function scopeLabel(scope) {
  if (scope === "loading") {
    return "Chargement";
  }

  if (scope === "delivery") {
    return "Livraison";
  }

  if (scope === "assignment") {
    return "Affectation";
  }

  if (scope === "truck") {
    return "Camion";
  }

  return scope;
}

export function severityLabel(severity) {
  if (severity === "error") {
    return "Alerte";
  }

  if (severity === "warning") {
    return "Avertissement";
  }

  return "Info";
}

export function highestErrorTone(entries) {
  if (entries.some((entry) => entry.severity === "error")) {
    return "danger";
  }

  if (entries.some((entry) => entry.severity === "warning")) {
    return "warning";
  }

  return "success";
}

export function shouldUseCompactMissionLayout(entry) {
  return (entry.filterKey === "non_assignable_orders" || entry.filterKey === "technical_precheck")
    && Boolean(entry.orderId)
    && (Boolean(entry.loadingItem) || entry.deliveryItems.length > 0);
}

export function buildCompactContext(entry) {
  return compact([
    entry.clientName,
    entry.truckName ? `Camion ${entry.truckName}` : (entry.truckId ? `Camion ${entry.truckId}` : ""),
  ]).join(" - ");
}

export function buildDetailedContext(entry) {
  return compact([
    entry.orderId ? `Mission ${entry.orderId}` : "",
    entry.deliveryId ? `Livraison ${entry.deliveryId}` : "",
    entry.truckName ? `Camion ${entry.truckName}` : (entry.truckId ? `Camion ${entry.truckId}` : ""),
    resolveSourceContextLabel(entry.sourceContext),
  ]).join(" - ");
}

export function getLocationStatusLabel(status) {
  if (status === "error") {
    return "À corriger";
  }

  if (status === "success") {
    return "Conforme";
  }

  return "Signalé";
}

export function formatTechnicalDetails(value) {
  if (value === null || value === undefined || value === "") {
    return "Aucun détail technique supplémentaire.";
  }

  if (typeof value === "string") {
    return value;
  }

  try {
    return JSON.stringify(value, null, 2);
  } catch (_error) {
    return String(value);
  }
}
